import { useEffect, useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { GridApiCommunity } from '@mui/x-data-grid/internals'
import { Control, FormProvider, SubmitHandler, useForm, useFormState } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import {
  BookingDriver,
  ReplaceBookingDriverDtoInput,
  useGetDriverByCurrentUserQuery,
  useReplaceBookingDriverMutation,
} from 'generated/graphql'

import ReplaceConfirmDialog from './ReplaceConfirmDialog'
import { ReplaceDriverForm } from './ReplaceDriverForm'
import ValidationSchema from './ValidationSchema'

interface IReplaceDriverDialogProps {
  bookingDriver: BookingDriver
  openDialog: boolean
  fplDataGridRef: React.MutableRefObject<any>
  gridApiRef: React.MutableRefObject<GridApiCommunity>
  handleCloseDialog: () => any
}

interface ISaveFormButtonProps {
  loading: boolean
  allocatedDriverName: string
  control: Control<ReplaceBookingDriverDtoInput, any>
  onSave: () => void
  bookingId: number
}

const SaveFormButton = ({
  loading,
  control,
  allocatedDriverName,
  onSave,
  bookingId,
}: ISaveFormButtonProps) => {
  const { isValid } = useFormState({ control })

  const [openConfirmModal, setOpenConfirmModal] = useState(false)

  const handleConfirmDialog = () => {
    setOpenConfirmModal(true)
  }

  return (
    <>
      <LoadingButton
        disabled={loading || !isValid}
        loading={loading}
        variant='contained'
        form='replace-allocated-driver-dialog-form'
        onClick={handleConfirmDialog}>
        Save
      </LoadingButton>

      <ReplaceConfirmDialog
        bookingId={bookingId}
        control={control}
        allocatedDriverName={allocatedDriverName}
        openConfirmModal={openConfirmModal}
        setOpenConfirmModal={setOpenConfirmModal}
        onConfirm={onSave}
      />
    </>
  )
}

const ReplaceDriverDialog = (props: IReplaceDriverDialogProps) => {
  const { bookingDriver, openDialog, handleCloseDialog, fplDataGridRef, gridApiRef } = props

  // States
  const methods = useForm<ReplaceBookingDriverDtoInput>({
    shouldUnregister: true,
    resolver: joiResolver(ValidationSchema),
    mode: 'onChange',
    defaultValues: {
      driverId: null,
      vehicleId: null,
      instruction: '',
      isShownInstruction: false,
    },
  })

  const { handleSubmit, setValue, control, watch } = methods

  const { data: driverData } = useGetDriverByCurrentUserQuery()

  const selectedDriverId = watch('driverId')
  const isSelfAllocation = selectedDriverId === driverData?.driverByCurrentUser?.id

  // Mutations
  const [replaceBookingDriver, { loading: replaceBookingDriverLoading }] =
    useReplaceBookingDriverMutation({
      onCompleted: (data) => {
        if (data.replaceBookingDriver?.id) {
          handleCloseDialog()
          toast.success(`Driver was replaced with success.`)
          fplDataGridRef.current.queryRefetchOnTableUpdate()
        } else {
          toast.error(`Something went wrong while replacement. Please try again later.`)
        }
      },
    })

  // Handlers
  const handleReplaceBookingDriver: SubmitHandler<ReplaceBookingDriverDtoInput> = async (
    formData,
  ) => {
    await replaceBookingDriver({
      variables: {
        input: {
          ...formData,
          bookingId: bookingDriver.bookingId,
          bookingDriverId: bookingDriver.id,
          isSelfAllocation,
        },
      },
    })
  }

  const getAllocatedSubDriverIds = () => {
    return Array.from(gridApiRef.current.getRowModels().values()).map((row) =>
      Number(row?.driverId),
    )
  }

  // Effects
  useEffect(() => {
    if (openDialog && bookingDriver.instruction) {
      setValue('instruction', bookingDriver.instruction)
    }
  }, [bookingDriver.instruction, openDialog, setValue])

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='md'
        open={openDialog}
        closeAfterTransition={false}
        aria-labelledby='replace-allocated-driver-dialog-title'>
        <FplDialogTitle id='replace-allocated-driver-dialog-title' onClose={handleCloseDialog}>
          <Typography mb={0} paragraph variant='h4'>
            Replace Allocated Driver
          </Typography>
        </FplDialogTitle>

        <DialogContent>
          <FormProvider {...methods}>
            <form id='replace-allocated-driver-dialog-form' noValidate>
              <ReplaceDriverForm
                bookingId={Number(bookingDriver.bookingId)}
                allocatedSubDriverIds={getAllocatedSubDriverIds()}
              />
            </form>
          </FormProvider>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={replaceBookingDriverLoading}
            variant='contained'
            color='grey'
            onClick={handleCloseDialog}>
            Cancel
          </Button>

          <SaveFormButton
            bookingId={Number(bookingDriver.bookingId)}
            control={control}
            allocatedDriverName={bookingDriver.driver.name}
            loading={replaceBookingDriverLoading}
            onSave={handleSubmit(handleReplaceBookingDriver)}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ReplaceDriverDialog
